import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { safeFetch } from "@/api/safeFetch";
import { ButtonSiguiente } from "@/components/ui";
import Alerta from "@/components/alert/Alerta";
import { AlertaType } from "@/types";

type Usuario = {
    usuario_id: number;
    nombre: string;
    apellidos: string;
    nombre_usuario: string;
    email: string;
    dni: string | null;
    nie: string | null;
    roles: string[];   // ✔ AHORA ES ARRAY
    verificado: number;
};

export default function SuperAdminUsuarios() {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [loading, setLoading] = useState(true);
    const [alerta, setAlerta] = useState<{ mensaje: string; type: AlertaType } | null>(null);

    // ======================
    // Cargar usuarios
    // ======================
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const cargarUsuarios = async () => {
            try {
                const res = await safeFetch("/api/superadmin/usuarios", {
                    headers: { Authorization: `Bearer ${token}` },
                    signal
                });

                if (res.status === 403) {
                    setAlerta({ mensaje: "Necesitas permisos de SuperAdmin", type: "error" });
                    return;
                }

                const data = await res.json();
                setUsuarios(data);
            } catch (err: any) {
                if (err.name !== "AbortError") {
                    setAlerta({ mensaje: "Error cargando usuarios", type: "error" });
                }
            } finally {
                setLoading(false);
            }
        };

        cargarUsuarios();

        return () => controller.abort();
    }, []);


    // ======================
    // Cambiar rol (roles[])
    // ======================
    const cambiarRol = async (id: number, nuevoRol: string) => {
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            const res = await safeFetch(`/api/superadmin/usuarios/${id}/rol`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ roles: [nuevoRol] }), // ✔ enviar array
                signal
            });

            if (!res.ok) throw new Error();

            setUsuarios(prev =>
                prev.map(u =>
                    u.usuario_id === id ? { ...u, roles: [nuevoRol] } : u
                )
            );

            setAlerta({ mensaje: "Rol actualizado correctamente ✅", type: "success" });
        } catch (err: any) {
            if (err.name !== "AbortError") {
                setAlerta({ mensaje: "Error actualizando rol", type: "error" });
            }
        }

        return () => controller.abort();
    };


    // ======================
    // Cambiar verificado
    // ======================
    const toggleVerificado = async (id: number, verificado: number, roles: string[]) => {
        if (roles.includes("superadmin")) {
            setAlerta({ mensaje: "No se puede deshabilitar a un SuperAdmin", type: "error" });
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        try {
            const nuevoVerificado = verificado === 1 ? 0 : 1;

            const res = await safeFetch(`/api/superadmin/usuarios/${id}/verificado`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ verificado: nuevoVerificado }),
                signal
            });

            if (!res.ok) throw new Error();

            setUsuarios(prev =>
                prev.map(u =>
                    u.usuario_id === id ? { ...u, verificado: nuevoVerificado } : u
                )
            );

            setAlerta({
                mensaje: `Usuario ${nuevoVerificado === 1 ? "habilitado" : "deshabilitado"} correctamente`,
                type: "success",
            });
        } catch (err: any) {
            if (err.name !== "AbortError") {
                setAlerta({ mensaje: "Error cambiando verificado", type: "error" });
            }
        }

        return () => controller.abort();
    };


    // ======================
    // Limpieza al desmontar
    // ======================
    useEffect(() => {
        return () => {
            setUsuarios([]);
            setLoading(false);
            setAlerta(null);
            window.scrollTo(0, 0);
        };
    }, []);

    // ======================
    // Render
    // ======================

    if (loading) return <p>Cargando usuarios...</p>;

    return (
        <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative text-zinc-800">

            {/* BOTONES */}
            <div className="absolute top-4 left-4 z-10">
                <ButtonSiguiente className="p-2" onClick={() => navigate("/backoffice")}>
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="absolute top-4 right-4 z-10">
                <ButtonSiguiente
                    onClick={() => navigate("/crearAdmin")}
                    className="bg-cyan-600 hover:bg-cyan-700 p-2"
                >
                    ➕ Crear Admin
                </ButtonSiguiente>
            </div>

            {/* CONTENEDOR CENTRAL */}
            <div className="w-full max-w-6xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-cyan-500">

                <h2 className="text-2xl font-bold text-center mb-6">
                    Gestión de Usuarios (SuperAdmin)
                </h2>

                {loading && (
                    <p className="text-center">Cargando usuarios...</p>
                )}

                {!loading && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-zinc-600 bg-white rounded">
                            <thead className="bg-zinc-300">
                                <tr>
                                    <th className="p-2">Usuario</th>
                                    <th className="p-2">Email</th>
                                    <th className="p-2">DNI/NIE</th>
                                    <th className="p-2">Rol</th>
                                    <th className="p-2">Verificado</th>
                                </tr>
                            </thead>

                            <tbody>
                                {usuarios.map(u => (
                                    <tr key={u.usuario_id} className="border-t">
                                        <td className="p-2">
                                            {u.nombre} {u.apellidos}
                                            <div className="text-xs text-gray-500">
                                                @{u.nombre_usuario}
                                            </div>
                                        </td>

                                        <td className="p-2">{u.email}</td>

                                        <td className="p-2">{u.dni || u.nie}</td>

                                        <td className="p-2">
                                            <select
                                                value={u.roles?.[0] ?? ""}   // ✔ ahora es un string
                                                onChange={(e) =>
                                                    cambiarRol(u.usuario_id, e.target.value)
                                                }
                                                className="p-1 rounded border bg-zinc-100 w-full"
                                            >
                                                <option value="usuario">Usuario</option>
                                                <option value="profesional">Profesional</option>
                                                <option value="admin">Admin</option>
                                                <option value="superadmin">SuperAdmin</option>
                                            </select>
                                        </td>


                                        <td className="p-2 text-center">
                                            <label
                                                htmlFor={`verificadoToggle-${u.usuario_id}`}
                                                className="inline-flex items-center cursor-pointer select-none"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`verificadoToggle-${u.usuario_id}`}
                                                    className="sr-only peer"
                                                    checked={u.verificado === 1}
                                                    onChange={() => toggleVerificado(u.usuario_id, u.verificado, u.roles)}
                                                />
                                                <div className="relative w-14 h-7 bg-zinc-300 rounded-full shadow-inner border border-zinc-200 
                                                   transition-all duration-300 ease-in-out
                                                   peer-checked:bg-gradient-to-r peer-checked:from-blue-500 peer-checked:to-cyan-400
                                                   hover:shadow-lg hover:scale-105">
                                                    <div className="absolute left-0 top-0.5 w-6 h-6 bg-white rounded-full shadow-md 
                                                     transform transition-transform duration-300 ease-in-out
                                                     peer-checked:translate-x-7 peer-checked:shadow-lg"></div>
                                                </div>
                                                <span className="ms-3 text-sm font-medium text-gray-700">
                                                    {u.verificado === 1 ? "Activo" : "Deshabilitado"}
                                                </span>
                                            </label>
                                        </td>


                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {alerta && (
                <Alerta
                    mensaje={alerta.mensaje}
                    type={alerta.type}
                    onClose={() => setAlerta(null)}
                />
            )}
        </div>
    );
};